import { Link } from '@tanstack/react-router'
import { Coins, Gift, Info, Receipt } from 'lucide-react'

import { PlanCards } from '@/components/billing/PlanCards'
import { PageHeader, Panel } from '@/components/ui/Panel'
import { EmptyState, QueryBoundary, TableSkeleton } from '@/components/ui/States'
import { useCredits } from '@/features/billing/useCredits'
import { useCreditHistory } from '@/features/billing/useCreditHistory'
import { usePaymentHistory } from '@/features/billing/usePaymentHistory'
import { DEFAULT_FREE_CREDITS, WHAT_CREDITS_BUY } from '@/features/billing/plans'
import { formatRelative } from '@/lib/utils/format'
import { cn } from '@/lib/utils/cn'

const PAYMENT_STATUS_STYLES: Record<string, string> = {
  success: 'bg-ok/10 text-ok border-ok/20',
  pending: 'bg-warn/10 text-warn border-warn/20',
  processing: 'bg-primary/10 text-primary border-primary/20',
  failed: 'bg-fail/10 text-fail border-fail/20',
  cancelled: 'bg-ink-muted/10 text-ink-muted border-line',
}

/** How one ledger row is described to the person it happened to. */
const MOVEMENT: Record<string, string> = {
  signup_grant: 'Welcome credits',
  purchase: 'Credits purchased',
  usage_charge: 'Used by a request',
  usage_reserve: 'Held for a running request',
  usage_release: 'Returned, unused',
  refund: 'Refunded',
  admin_adjustment: 'Adjusted by support',
}

function Figure({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string
  value: number
  hint: string
  tone?: 'default' | 'strong'
}) {
  return (
    <div className="px-4 py-3" data-testid={`figure-${label.toLowerCase()}`}>
      <p className="text-xs text-ink-muted">{label}</p>
      <p
        className={
          tone === 'strong'
            ? 'mt-1 text-2xl font-semibold tabular-nums text-ink'
            : 'mt-1 text-2xl font-semibold tabular-nums text-ink-dim'
        }
      >
        {value}
      </p>
      <p className="mt-0.5 text-xs text-ink-muted">{hint}</p>
    </div>
  )
}

/**
 * Credits: what you have, what you have spent it on, and how to get more.
 *
 * The page exists whether or not the platform is charging anybody. While billing is off it
 * says so plainly instead of hiding — a product that will charge for something should be
 * willing to show the terms before it starts, and a blank page is not an answer to "what
 * will this cost me?".
 */
export function BillingPage() {
  const credits = useCredits()
  const history = useCreditHistory()
  const payments = usePaymentHistory()
  const billing = credits.enabled
  // The grant the *server* makes, so a deployment that gives 3 cannot have a page promising
  // 25. The constant is only a fallback for the moment before the first response lands.
  const grant = credits.data?.signup_grant_credits ?? DEFAULT_FREE_CREDITS
  const answered = credits.isSuccess

  return (
    <div>
      <PageHeader
        title="Credits"
        description="Every question and review spends credits, charged on what the model actually used. Nothing is charged for a request that does not reach the model."
      />

      {answered && !billing ? (
        <p
          data-testid="billing-not-live"
          className="mb-4 flex items-start gap-2.5 rounded-lg border border-line bg-raised px-4 py-3
            text-sm text-ink-dim"
        >
          <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />
          <span>
            <span className="font-medium text-ink">Credits are not being charged yet.</span>{' '}
            Everything you run today is free while payments are finished. When charging
            starts, every account is given{' '}
            <span className="tabular-nums text-ink">{grant}</span> free credits to begin
            with, and you will see the balance here before anything is spent.
          </span>
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Panel
          title="Balance"
          description={billing ? 'Updated as each request settles.' : 'Shown once charging begins.'}
          bodyClassName="grid grid-cols-2 divide-x divide-line border-t border-line sm:grid-cols-4 sm:divide-y-0"
        >
          <Figure
            label="Total Credit"
            value={grant}
            hint="Granted on signup"
          />
          <Figure
            label="Spent Credit"
            value={Math.max(0, grant - (credits.data?.balance_credits ?? 0))}
            hint="Consumed by requests"
          />
          <Figure
            label="Available"
            value={credits.data?.available_credits ?? 0}
            hint="What a new request may spend"
            tone="strong"
          />
          <Figure
            label="Held"
            value={credits.data?.reserved_credits ?? 0}
            hint="Claimed by a running request"
          />
        </Panel>

        <Panel title="What a credit buys" bodyClassName="divide-y divide-line/60 border-t border-line">
          {WHAT_CREDITS_BUY.map((item) => (
            <div key={item.label} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <span className="min-w-0 text-sm text-ink-dim">{item.label}</span>
              <span className="shrink-0 text-sm font-medium tabular-nums text-ink">
                ~{item.credits}
              </span>
            </div>
          ))}
          <p className="px-4 py-2.5 text-xs text-ink-muted">
            Measured from real usage. A long review of a large diff costs more; the exact
            amount is charged from what the model reports, never estimated.
          </p>
        </Panel>
      </div>

      <div className="mt-4">
        <Panel
          title={
            <span className="flex items-center gap-2">
              <Gift aria-hidden="true" className="h-4 w-4 text-ok" />
              Free credits
            </span>
          }
          description="Given once, on the house, so you can try the assistant before paying for it."
        >
          <p className="px-4 py-3 text-sm text-ink-dim">
            Every new account starts with{' '}
            <span className="font-medium tabular-nums text-ink">{grant}</span> free credits —
            roughly{' '}
            <span className="tabular-nums text-ink">{Math.floor(grant / 5)}</span> questions
            or one full pull-request review. No card, no expiry.
          </p>
        </Panel>
      </div>

      <div className="mt-6">
        <h2 className="text-md font-semibold text-ink">Top up</h2>
        <p className="mt-1 text-sm text-ink-muted">
          One-off packs. Credits do not expire and there is no subscription.
        </p>
        <PlanCards className="mt-4" />
      </div>

      <div className="mt-6">
        <Panel
          title="Credit history"
          description="Every movement, newest first — grants, charges, and anything returned unused."
          actions={
            <Link to="/executions" className="text-xs text-primary underline-offset-2 hover:underline">
              See the calls behind them
            </Link>
          }
        >
          <QueryBoundary
            isLoading={history.isLoading}
            error={history.error}
            loadingFallback={<TableSkeleton rows={4} columns={3} />}
          >
            {() => {
              const rows = history.data?.transactions ?? []
              return rows.length === 0 ? (
                <EmptyState
                  icon={Coins}
                  title={billing ? 'Nothing yet.' : 'Nothing yet — credits are not being charged.'}
                  hint={
                    billing
                      ? 'Your welcome credits and every charge will appear here.'
                      : 'When charging begins, your welcome credits will be the first row.'
                  }
                />
              ) : (
                <ul className="divide-y divide-line/60">
                  {rows.map((row) => (
                    <li
                      key={row.id}
                      className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-ink-dim">
                          {MOVEMENT[row.type] ?? row.type}
                        </span>
                        <span className="block text-xs text-ink-muted">
                          {formatRelative(row.created_at)}
                          {row.status === 'reserved' ? ' · still running' : ''}
                        </span>
                      </span>
                      <span
                        className={
                          row.amount_credits >= 0
                            ? 'shrink-0 tabular-nums font-medium text-ok'
                            : 'shrink-0 tabular-nums font-medium text-ink'
                        }
                      >
                        {row.amount_credits >= 0 ? '+' : ''}
                        {row.amount_credits}
                      </span>
                    </li>
                  ))}
                </ul>
              )
            }}
          </QueryBoundary>
        </Panel>
      </div>

      <div className="mt-6">
        <Panel
          title="Payment history"
          description="Invoices and records of all credit purchases through Razorpay."
        >
          <QueryBoundary
            isLoading={payments.isLoading}
            error={payments.error}
            loadingFallback={<TableSkeleton rows={3} columns={3} />}
          >
            {() => {
              const rows = payments.data?.payments ?? []
              return rows.length === 0 ? (
                <EmptyState
                  icon={Receipt}
                  title="No payment history yet."
                  hint="When you top up credits, your invoices and receipts will appear here."
                />
              ) : (
                <ul className="divide-y divide-line/60">
                  {rows.map((row) => (
                    <li
                      key={row.id}
                      className="flex items-center justify-between gap-4 px-4 py-3 text-sm"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-ink">{row.package_name} Pack</span>
                          <span
                            className={cn(
                              'inline-flex items-center rounded-full border px-2 py-0.5 text-micro font-medium uppercase tracking-wide',
                              PAYMENT_STATUS_STYLES[row.status] ?? 'border-line text-ink-muted',
                            )}
                          >
                            {row.status}
                          </span>
                        </div>
                        <span className="block font-mono text-xs text-ink-muted mt-0.5">
                          ID: {row.razorpay_order_id} · {formatRelative(row.created_at)}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="block font-semibold tabular-nums text-ink">
                          ₹{row.amount_paise / 100}
                        </span>
                        <span className="block text-xs font-medium text-ok">
                          +{row.credits} credits
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )
            }}
          </QueryBoundary>
        </Panel>
      </div>
    </div>
  )
}
